import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useEffect } from "react";
import { Menu, X, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CookieBanner } from "@/components/CookieBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { fetchAuthorProfile, getAuthorSocial, type AuthorSocialLinks } from "@/lib/author";

export function SiteShell({
  children,
  social: initialSocial,
}: {
  children: React.ReactNode;
  social?: AuthorSocialLinks;
}) {
  const [menu, setMenu] = useState(false);
  const [fetchedSocial, setFetchedSocial] = useState(() => getAuthorSocial());

  useEffect(() => {
    if (initialSocial) return;
    let active = true;
    fetchAuthorProfile()
      .then((profile) => {
        if (active) setFetchedSocial(getAuthorSocial(profile));
      })
      .catch((error: unknown) => {
        console.warn("Could not load author social links:", error);
      });
    return () => {
      active = false;
    };
  }, [initialSocial]);
  const social = initialSocial ?? fetchedSocial;

  return (
    <div className="site-shell">
      <CookieBanner />
      <header className="site-header">
        <div className="container-wide header-inner">
          <Link to="/" className="brand-logo-link" aria-label="BuildWithJIT home">
            <img
              src="/brand/logo-horizontal-light-no-tagline.svg"
              alt="BuildWithJIT"
              className="brand-full-logo logo-light"
              width="157"
              height="38"
            />
            <img
              src="/brand/logo-horizontal-dark-no-tagline.svg"
              alt="BuildWithJIT"
              className="brand-full-logo logo-dark"
              width="157"
              height="38"
            />
          </Link>
          <nav className={menu ? "main-nav is-open" : "main-nav"} aria-label="Main navigation">
            <Link to="/" onClick={() => setMenu(false)}>
              Home
            </Link>
            <Link to="/articles" onClick={() => setMenu(false)}>
              Articles
            </Link>
            <Link to="/about" onClick={() => setMenu(false)}>
              About
            </Link>
            <Link to="/contact" onClick={() => setMenu(false)}>
              Contact
            </Link>

            {/* Mobile Navigation Panel */}
            <div className="mobile-nav-panel">
              <div className="mobile-nav-divider" />
              <div className="mobile-theme-row">
                <span className="mobile-theme-label">Appearance / Theme</span>
                <ThemeToggle showLabels size="md" className="mobile-theme-toggle" />
              </div>
              <Button asChild className="w-full mt-3">
                <Link to="/contact" onClick={() => setMenu(false)}>
                  <Mail size={15} className="mr-1.5" /> Get in Touch
                </Link>
              </Button>
            </div>
          </nav>

          <div className="header-actions">
            <ThemeToggle className="header-theme-toggle" />
            <Button asChild className="header-chat">
              <Link to="/contact">
                <Mail size={15} /> Get in Touch
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="mobile-menu"
              onClick={() => setMenu(!menu)}
              aria-label="Toggle menu"
            >
              {menu ? <X /> : <Menu />}
            </Button>
          </div>
        </div>
      </header>
      <main>{children}</main>
      <footer className="site-footer">
        <div className="container-wide footer-main">
          <div>
            <Link
              to="/"
              className="brand-logo-link footer-brand-link"
              aria-label="BuildWithJIT home"
            >
              <img
                src="/brand/logo-horizontal-light-no-tagline.svg"
                alt="BuildWithJIT"
                className="brand-full-logo logo-light"
                width="190"
                height="46"
              />
              <img
                src="/brand/logo-horizontal-dark-no-tagline.svg"
                alt="BuildWithJIT"
                className="brand-full-logo logo-dark"
                width="190"
                height="46"
              />
            </Link>
            <p>
              Deep-dive architecture guides, production benchmarks, and practical workflows for AI
              automation, RAG pipelines, voice AI agents, and systems engineering.
            </p>
            <div className="footer-links">
              <a href={social.linkedin} target="_blank" rel="me noopener noreferrer">
                LinkedIn
              </a>
              <a href={social.github} target="_blank" rel="me noopener noreferrer">
                GitHub
              </a>
            </div>
          </div>
          <div>
            <div className="footer-column-title">Explore</div>
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/articles">Articles & Architecture</Link>
              <Link to="/about">About Us</Link>
              <Link to="/author/jignesh-mevada">Author Profile</Link>
              <Link to="/contact">Contact Us</Link>
            </div>
          </div>
          <div>
            <div className="footer-column-title">Legal & Standards</div>
            <div className="footer-links">
              <Link to="/editorial-policy">Editorial Policy</Link>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-condition">Terms & Conditions</Link>
            </div>
          </div>
        </div>
        <div className="container-wide footer-bottom">
          <span>© {new Date().getFullYear()} BuildWithJIT. All rights reserved.</span>
          <span>Made with intent in India.</span>
        </div>
      </footer>
    </div>
  );
}
